import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Gift, Coins, Loader2, Sparkles, Mic2, Crown, Flame, Trophy, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';



// Effect icons and descriptions
const EFFECT_INFO = {
  host_shoutout: { icon: Mic2, label: 'Shoutout', color: 'text-purple-400' },
  vip_highlight: { icon: Crown, label: 'VIP 5min', color: 'text-amber-400' },
  pin_message: { icon: Flame, label: 'Pin 60s', color: 'text-orange-400' },
  profile_badge: { icon: Trophy, label: 'Badge 24h', color: 'text-yellow-400' }
};

export function GiftPanel({ streamId, getAuthHeaders, onGiftSent }) {
  const [gifts, setGifts] = useState([]);
  const [wallet, setWallet] = useState({ coin_balance: 0 });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  const [selectedGift, setSelectedGift] = useState(null);
  const [pinMessage, setPinMessage] = useState('');
  const [showMessageInput, setShowMessageInput] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [giftsRes, walletRes] = await Promise.all([
        fetch(`${API}/gifts`),
        fetch(`${API}/coins/wallet`, { headers: getAuthHeaders() })
      ]);
      
      if (giftsRes.ok) {
        const data = await giftsRes.json();
        setGifts(data.gifts || []);
      }
      if (walletRes.ok) {
        setWallet(await walletRes.json());
      }
    } catch (error) {
      console.error('Error fetching gift data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGiftClick = (gift) => {
    if (wallet.coin_balance < gift.coin_cost) {
      toast.error('Not enough coins! Buy more to send gifts.');
      return;
    }

    // If gift has pin_message effect, show message input
    if (gift.effect === 'pin_message') {
      setSelectedGift(gift);
      setShowMessageInput(true);
      return;
    }

    // Otherwise send directly
    handleSendGift(gift);
  };

  const handleSendGift = async (gift, message = null) => {
    if (wallet.coin_balance < gift.coin_cost) {
      toast.error('Not enough coins! Buy more to send gifts.');
      return;
    }

    setSending(gift.gift_id);
    try {
      const body = {
        gift_id: gift.gift_id,
        quantity: 1
      };
      
      // Add message for pin_message effect
      if (message) {
        body.message = message;
      }

      const response = await fetch(`${API}/live/streams/${streamId}/gift`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(body)
      });

      const result = await safeJson(response);

      if (result.ok && result.data) {
        const data = result.data;
        
        // Show effect-specific success message
        if (data.effect) {
          const effectType = data.effect.effect_type;
          if (effectType === 'host_shoutout') {
            toast.success('🎤 Mic Drop! Host will shoutout you!');
          } else if (effectType === 'vip_highlight') {
            toast.success('👑 VIP Crown! Your messages are highlighted for 5 minutes!');
          } else if (effectType === 'pin_message') {
            toast.success('🔥 Stage Flame! Your message is pinned!');
          } else if (effectType === 'profile_badge') {
            toast.success('🏆 Soundstage Trophy! You earned a 24h badge!');
          }
        } else {
          toast.success(`Sent ${gift.emoji} ${gift.name}!`);
        }
        
        setWallet(prev => ({ ...prev, coin_balance: prev.coin_balance - gift.coin_cost }));
        setShowMessageInput(false);
        setPinMessage('');
        setSelectedGift(null);
        onGiftSent?.(data);
      } else {
        toast.error(result.error || 'Failed to send gift');
      }
    } catch (error) {
      console.error('Error sending gift:', error);
      toast.error('Failed to send gift');
    } finally {
      setSending(null);
    }
  };

  const handleSendPinMessage = () => {
    if (!selectedGift || !pinMessage.trim()) {
      toast.error('Please enter a message to pin');
      return;
    }
    handleSendGift(selectedGift, pinMessage.trim());
  };

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Group gifts by category, including recognition
  const categoryOrder = ['basic', 'recognition', 'premium', 'elite', 'legendary'];
  const categoryLabels = {
    basic: 'Basic',
    recognition: '⭐ Special Recognition',
    premium: 'Premium',
    elite: 'Elite',
    legendary: 'Legendary'
  };
  
  const groupedGifts = categoryOrder.reduce((acc, cat) => {
    acc[cat] = gifts.filter(g => g.category === cat);
    return acc;
  }, {});

  return (
    <div className="p-4 space-y-4" data-testid="gift-panel">
      {/* Balance */}
      <div className="flex items-center justify-between p-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
        <div className="flex items-center gap-2">
          <Coins className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-medium">{wallet.coin_balance.toLocaleString()} coins</span>
        </div>
        <Button size="sm" variant="ghost" className="text-amber-400 text-xs">
          Get More
        </Button>
      </div>

      {/* Pin Message Input (for Stage Flame) */}
      {showMessageInput && selectedGift && (
        <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/30 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-orange-400">
              🔥 Pin your message
            </span>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={() => {
                setShowMessageInput(false);
                setSelectedGift(null);
                setPinMessage('');
              }}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <Input
            value={pinMessage}
            onChange={(e) => setPinMessage(e.target.value)}
            placeholder="Type your message to pin..."
            maxLength={100}
            className="bg-background/50"
          />
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">{pinMessage.length}/100</span>
            <Button
              size="sm"
              onClick={handleSendPinMessage}
              disabled={!pinMessage.trim() || sending}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Pin for 500 coins'}
            </Button>
          </div>
        </div>
      )}

      {/* Gifts Grid */}
      <div className="space-y-3 max-h-[350px] overflow-y-auto">
        {categoryOrder.map(category => (
          groupedGifts[category]?.length > 0 && (
            <div key={category}>
              <p className={cn(
                "text-xs uppercase mb-2",
                category === 'recognition' ? "text-purple-400 font-medium" : "text-muted-foreground"
              )}>
                {categoryLabels[category] || category}
              </p>
              <div className="grid grid-cols-4 gap-2">
                {groupedGifts[category].map(gift => {
                  const effectInfo = gift.effect ? EFFECT_INFO[gift.effect] : null;
                  const EffectIcon = effectInfo?.icon;
                  
                  return (
                    <button
                      key={gift.gift_id}
                      onClick={() => handleGiftClick(gift)}
                      disabled={sending || wallet.coin_balance < gift.coin_cost}
                      className={cn(
                        "flex flex-col items-center p-2 rounded-lg border transition-all relative",
                        wallet.coin_balance >= gift.coin_cost
                          ? gift.effect 
                            ? "border-purple-500/30 hover:border-purple-500/70 hover:bg-purple-500/10"
                            : "border-border hover:border-primary/50 hover:bg-primary/10"
                          : "border-border opacity-50 cursor-not-allowed",
                        sending === gift.gift_id && "animate-pulse"
                      )}
                      data-testid={`gift-btn-${gift.gift_id}`}
                    >
                      {/* Effect indicator */}
                      {effectInfo && (
                        <div className="absolute -top-1 -right-1">
                          <Sparkles className={cn("w-3 h-3", effectInfo.color)} />
                        </div>
                      )}
                      
                      <span className="text-2xl mb-1">{gift.emoji}</span>
                      <span className="text-xs truncate w-full text-center">{gift.name}</span>
                      <span className="text-xs text-amber-400">{gift.coin_cost}</span>
                      
                      {/* Effect label */}
                      {effectInfo && (
                        <span className={cn("text-[9px] mt-0.5", effectInfo.color)}>
                          {effectInfo.label}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )
        ))}
      </div>
      
      {/* Special gifts info */}
      <div className="text-xs text-muted-foreground border-t border-border pt-3">
        <p className="flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-purple-400" />
          Special recognition gifts trigger instant perks!
        </p>
      </div>
    </div>
  );
}
